import { AsyncStorage } from '@utils/storage';
import { getExpiryTimestamp, isUrlExpired } from '@utils/urlExpiry';

/**
 * Avatar URL caching (TA-9793).
 *
 * S3 presigned avatar URLs are valid for 7 days (X-Amz-Expires=604800), so a
 * URL we fetched recently does not need re-validating. We record the fetch
 * timestamp alongside the URL under a parallel `${key}__fetchedAt` key and skip
 * the expiry check entirely while the URL is younger than the TTL.
 *
 * This also fixes a latent bug: `isUrlExpired` returns `true` for any URL that
 * lacks `X-Amz-Date`/`X-Amz-Expires` (e.g. CDN thumbnail URLs), which forced a
 * re-fetch on every check. The timestamp short-circuit prevents that.
 */
export const AVATAR_URL_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const TS_SUFFIX = '__fetchedAt';

const tsKey = (key: string) => `${key}${TS_SUFFIX}`;

/**
 * Pure decision: should we re-fetch this cached avatar URL?
 *
 * - No URL → always refetch.
 * - Fetched within the TTL window → trust it, skip URL parsing.
 * - Otherwise fall back to parsing the URL's own S3 expiry (covers legacy
 *   cache entries written before timestamps existed).
 */
export const shouldRefetchAvatarUrl = (
  url: string | null | undefined,
  fetchedAtMs: number | null,
  nowMs: number
): boolean => {
  if (!url) return true;
  if (fetchedAtMs != null && nowMs - fetchedAtMs < AVATAR_URL_TTL_MS) {
    return false;
  }
  return isUrlExpired(url, new Date(nowMs));
};

/**
 * Derive the timestamp to record for a URL we are caching but did NOT just
 * fetch fresh (e.g. a thumbnail read out of the persisted Apollo cache, or a
 * URL whose signature may be old).
 *
 * `shouldRefetchAvatarUrl` trusts an entry for `AVATAR_URL_TTL_MS` after its
 * timestamp, so for presigned S3 URLs we stamp `expiry - TTL`: the trust window
 * then ends exactly when the signature really expires, whatever `X-Amz-Expires`
 * the URL carries (stamping the minted time alone only matches for 7-day
 * signatures — a shorter one would be trusted long after it died). If only
 * `X-Amz-Date` parses we fall back to the minted time. For URLs without S3
 * params (e.g. CDN thumbnails, which don't expire) we treat `nowMs` as the
 * fetch time.
 */
export const avatarFetchedAtFromUrl = (url: string, nowMs: number): number => {
  const expiresAt = getExpiryTimestamp(url);
  if (expiresAt != null) return expiresAt - AVATAR_URL_TTL_MS;

  const amzDate = new URLSearchParams(url.split('?')[1]).get('X-Amz-Date');
  if (!amzDate) return nowMs;
  const minted = Date.parse(
    `${amzDate.slice(0, 4)}-${amzDate.slice(4, 6)}-${amzDate.slice(
      6,
      8
    )}T${amzDate.slice(9, 11)}:${amzDate.slice(11, 13)}:${amzDate.slice(
      13,
      15
    )}Z`
  );
  return Number.isNaN(minted) ? nowMs : minted;
};

export const getAvatarUrlTimestamp = async (
  key: string
): Promise<number | null> => {
  const raw = await AsyncStorage.getItem(tsKey(key));
  if (!raw) return null;
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

export const setAvatarUrlWithTimestamp = async (
  key: string,
  url: string,
  nowMs: number
): Promise<void> => {
  await AsyncStorage.multiSet([
    [key, url],
    [tsKey(key), String(avatarFetchedAtFromUrl(url, nowMs))],
  ]);
};

/**
 * Batch variant: write many URL + timestamp pairs in a single `multiSet`.
 * Used when persisting a freshly-fetched batch so the writes are awaited
 * together rather than fired-and-forgotten in a loop.
 */
export const setAvatarUrlsWithTimestamp = async (
  entries: { key: string; url: string }[],
  nowMs: number
): Promise<void> => {
  if (entries.length === 0) return;
  const pairs: [string, string][] = [];
  entries.forEach(({ key, url }) => {
    pairs.push(
      [key, url],
      [tsKey(key), String(avatarFetchedAtFromUrl(url, nowMs))]
    );
  });
  await AsyncStorage.multiSet(pairs);
};

/**
 * Build the `setAvatarUrlsWithTimestamp` entries for a batch of avatar
 * thumbnail results, skipping any id whose `thumbnail` is null, empty, or
 * whitespace-only (TA-10290).
 *
 * `AvatarThumbnailCachePrefix` keys persist in AsyncStorage across app
 * restarts, independent of Apollo's in-memory cache. A validation refetch can
 * return `thumbnail: null` for an avatar that already has a valid persisted
 * URL — writing `thumbnail ?? ''` unconditionally would blank a good cached
 * URL with an empty string. Dropping null/empty/blank entries here leaves the
 * previously-cached URL in place; a later authoritative non-empty thumbnail
 * still overwrites it normally.
 */
export const buildAvatarThumbnailCacheEntries = (
  items: { id: string; thumbnail?: string | null }[],
  keyPrefix: string
): { key: string; url: string }[] =>
  items
    .filter(
      (item): item is { id: string; thumbnail: string } =>
        !!item.thumbnail?.trim()
    )
    .map((item) => ({
      key: keyPrefix + item.id,
      url: item.thumbnail.trim(),
    }));
